import BaseEntity from "../../core/entity/BaseEntity";
import Entity from "../../core/entity/Entity";
import { BassComposer } from "./Composers/BassComposer";
import { DrumsComposer } from "./Composers/DrumsComposer";
import { MasterComposer } from "./Composers/MasterComposer";
import { StabComposer } from "./Composers/StabComposer";
import { InstrumentPlayer } from "./InstrumentPlayer";
import { BassInstrument } from "./Instruments/BassInstrument";
import { DrumsInstrument } from "./Instruments/DrumsInstrument";
import { Instrument } from "./Instruments/Instrument";
import { StabInstrument } from "./Instruments/StabInstrument";
import MusicController from "./MusicController";
import { TempoClock } from "./TempoClock";

/**
 * Music parameter interface defining the emotional and technical
 * characteristics of the generated music.
 */
export interface MusicParams {
  /** Intensity level (0-1): calm exploration → frantic combat */
  intensity: number;
  /** Tempo in beats per minute */
  tempo: number;
}

/**
 * Central music system that orchestrates all musical components.
 *
 * Creates and manages the TempoClock, MusicController, MasterComposer,
 * and all structured instruments. Provides the interface for other game
 * systems to control music parameters and tempo changes.
 *
 * Architecture:
 * - Creates child entities: TempoClock, MusicController, MasterComposer
 * - MasterComposer coordinates all structured composers and provides musical context
 * - Instruments work with composers for phrase-aware, harmonic music
 * - Manages global music parameters (intensity, tempo)
 * - Provides tempo change interface with smooth transitions
 */
export default class MusicGenerator extends BaseEntity implements Entity {
  tags = ["musicGenerator"];
  id = "musicGenerator";
  pausable = false; // Keep music playing during game pause

  /** Current music parameters affecting all composers */
  private params: MusicParams = {
    intensity: 0.3,
    tempo: 130, // Default BPM
  };

  /**
   * Get the TempoClock entity for access by other systems like BloomController
   */
  get tempoClock(): TempoClock {
    const maybeTempoClock = this.game?.entities.getById("tempoClock");
    if (maybeTempoClock instanceof TempoClock) {
      return maybeTempoClock;
    }
    throw new Error("TempoClock entity is not available");
  }

  /**
   * Initialize the structured music system with all required components.
   * Creates timing, control, master composer, and structured instruments.
   */
  onAdd() {
    if (!this.game?.audio) {
      throw new Error("No audio context available for music generator");
    }
    if (!this.game?.masterGain) {
      throw new Error("No master gain node available for music generator");
    }

    // Initialize core music timing and control
    this.addChild(new TempoClock(this.params.tempo));
    this.addChild(new MusicController());

    // Initialize master composer for coordinated music generation
    const masterComposer = new MasterComposer();
    this.addChild(masterComposer);

    // Add structured instruments with their composers using InstrumentPlayer
    this.addChild(
      new InstrumentPlayer(
        new BassComposer(),
        masterComposer,
        new BassInstrument(this.game.audio, this.game.masterGain),
      ),
    );
    this.addChild(
      new InstrumentPlayer(
        new DrumsComposer(),
        masterComposer,
        new DrumsInstrument(this.game.audio, this.game.masterGain),
      ),
    );
    // this.addChild(
    //   new InstrumentPlayer(
    //     new StabComposer(masterComposer),
    //     masterComposer,
    //     new StabInstrument(this.game.audio, this.game.masterGain),
    //   ),
    // );
  }

  getInstruments() {
    return this.children.filter((child) => child instanceof InstrumentPlayer);
  }

  /**
   * Update music parameters that affect all composers.
   * Changes are applied immediately via MasterComposer and composers will use
   * the new values on their next beat generation.
   */
  setParams(newParams: Partial<MusicParams>) {
    this.params = { ...this.params, ...newParams };

    // Apply tempo changes to the timing system
    if (this.tempoClock && newParams.tempo !== undefined) {
      this.tempoClock.setTempo(this.params.tempo);
    }

    // Update master composer with new parameters
    const masterComposer = this.game?.entities.getById(
      "masterComposer",
    ) as MasterComposer;
    if (masterComposer) {
      masterComposer.setParams({
        intensity: this.params.intensity,
        tempo: this.params.tempo,
      });
    }
  }

  getParams(): MusicParams {
    return { ...this.params };
  }

  /**
   * Request a tempo change for dynamic music response.
   * This is the interface that game systems should use to change tempo.
   */
  requestTempoChange(
    newBpm: number,
    options?: {
      /** When to apply the change (defaults to next measure) */
      atBeat?: number;
      /** Whether to apply immediately (defaults to false) */
      immediate?: boolean;
    },
  ): void {
    const tempoClock = this.tempoClock;
    if (!tempoClock) return;

    if (options?.immediate) {
      tempoClock.setTempo(newBpm);
    } else {
      // Default: apply at the next measure boundary for musical smoothness
      const currentBeat = tempoClock.getCurrentBeatNumber();
      const nextMeasure = Math.ceil(currentBeat / 4) * 4;
      const targetBeat = options?.atBeat ?? nextMeasure;

      tempoClock.scheduleTempo(newBpm, targetBeat);
    }

    // Update internal tempo parameter to match
    this.params.tempo = newBpm;
  }

  /**
   * Gradually change tempo over time (accelerando/ritardando)
   */
  requestTempoRamp(
    fromBpm: number,
    toBpm: number,
    durationBeats: number,
  ): void {
    const tempoClock = this.tempoClock;
    if (!tempoClock) return;

    const currentBeat = tempoClock.getCurrentBeatNumber();
    const steps = Math.max(4, Math.floor(durationBeats / 2)); // At least 4 steps, every 2 beats

    for (let i = 0; i <= steps; i++) {
      const progress = i / steps;
      const interpolatedBpm = fromBpm + (toBpm - fromBpm) * progress;
      const targetBeat = currentBeat + durationBeats * progress;

      tempoClock.scheduleTempo(interpolatedBpm, targetBeat);
    }

    // Update internal tempo parameter to final value
    this.params.tempo = toBpm;
  }

  /**
   * Get current tempo in BPM for game systems
   */
  getCurrentTempo(): number {
    return this.tempoClock?.getCurrentTempo() ?? this.params.tempo;
  }
}
